import React, { useState, useEffect } from 'react'
import { Modal, Table, Button, Switch, Form, Input, message, Select, Tag } from 'antd'
import { PlusOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons'
import { hookService } from 'services/hookService'
import HookDetailsModal from '../HookDetailsModal'
import {
  AddressField,
  onHookTypeChange,
  toFormValues,
  toPayload,
  triggerLabel,
  triggerOptions,
  TestSendButton,
} from 'containers/Setup/Hooks/planningRisks'
import i18n from 'i18next'
import { withNamespaces } from 'react-i18next'
import { inject, observer } from 'mobx-react'
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter'
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json'
import { arta } from 'react-syntax-highlighter/dist/esm/styles/hljs'

SyntaxHighlighter.registerLanguage('json', json)

// Resposta esperada do webhook "Integrar ordens de produção" (tipo 14)
const INTEGRATION_RESPONSE_SAMPLE = `{
  "synced": 10,
  "codes": ["000123", "000124"],
  "message": "opcional"
}`

export const HOOK_EVENTS = [
  {
    id: 0,
    name: 'ProductionAlert',
    description: 'Triggered when any work station alert is invoked by any operator.',
  },
  {
    id: 1,
    name: 'WorkOrderPlanned',
    description: 'Triggered when a work order is schedule, unschedule, shift.',
  },
  {
    id: 2,
    name: 'WorkOrderExecutionBegin',
    description: 'Triggered when a work order start the production.',
  },
  {
    id: 3,
    name: 'WorkOrderExecutionPaused',
    description: 'Triggered when a work order pause the production.',
  },
  {
    id: 4,
    name: 'WorkOrderExecutionEnded',
    description: 'Triggered when the flow of a work order is concluded.',
  },
  { id: 5, name: 'WorkOrderReset', description: 'Triggered when a work order is reset.' },
  {
    id: 6,
    name: 'WorkOrderConsumedRawMaterialUpdated',
    description:
      'Triggered when a consumed raw material has any update, even when the work order is closed.',
  },
  {
    id: 7,
    name: 'WorkOrderExecutionResumed',
    description: 'Triggered when a paused work order is resumed.',
  },
  {
    id: 8,
    name: 'MaterialSupplyUpdated',
    description: 'Triggered when a material supply is payed.',
  },
  {
    id: 9,
    name: 'WorkOrderProducedQuantityUpdated',
    description:
      'Triggered when the work order produced quantity is updated though the PlantScanner Portal.',
  },
  {
    id: 10,
    name: 'CanWorkOrderFinalize',
    description:
      'This hook is triggered every time an operator tries to complete the work order, you can use this hook when you want to perform custom checks whether or not that specific order can be completed.',
  },
  {
    id: 11,
    name: 'CanWorkOrderTrackQuantity',
    description:
      'This hook is activated whenever an operator attempts to record the produced good or bad quantities in a work order. You can utilize this hook to perform custom checks to determine whether a specific order can be tracked.',
  },
  {
    id: 12,
    name: 'PrintedTag',
    description: 'Triggered when an operator prints a tag',
  },
  {
    id: 13,
    name: 'CanWorkOrderStartExecution',
    description:
      'Triggered every time an operator tries to start a work order, you can use this hook when you want to perform custom checks whether or not that specific order can be started.',
  },
  {
    id: 14,
    name: 'IntegrateWorkOrders',
    description:
      'Called when someone taps "Sync now" on the home screen. The webhook pulls the production orders from the ERP and answers how many were synced and which codes.',
  },
  {
    id: 15,
    name: 'PlanningRisks',
    description:
      'E-mail with the orders that will miss the delivery date, are late on the schedule or lack material: daily summary at 7am and an alert when a new risk shows up.',
  },
]

export const HooksModal = withNamespaces()(
  inject('rootStore')(
    observer(({ isOpen, onClose, t, rootStore }) => {
      const [hooks, setHooks] = useState([])
      const [loading, setLoading] = useState(false)
      const [form] = Form.useForm()
      const [editingHook, setEditingHook] = useState(null)
      const [selectedHookId, setSelectedHookId] = useState(null)
      const [detailsModalOpen, setDetailsModalOpen] = useState(false)
      const [showForm, setShowForm] = useState(false)

      const { workStations } = rootStore.workStationStore

      useEffect(() => {
        if (isOpen) {
          loadHooks()
        }
      }, [isOpen])

      const loadHooks = async () => {
        try {
          setLoading(true)
          const response = await hookService.getAllHooks()
          // Sort hooks by active status first
          const sortedHooks = response.data.sort((a, b) => b.id - a.id)
          setHooks(sortedHooks)
        } catch (error) {
          message.error(t('hooks.loadError'))
        } finally {
          setLoading(false)
        }
      }

      const handleCreate = () => {
        setEditingHook(null)
        form.resetFields()
        form.setFieldsValue({
          isActive: true,
          allWorkStations: true,
          workStationIds: [],
        })
        setShowForm(true)
      }

      const handleEdit = (hook) => {
        setEditingHook(hook)
        form.setFieldsValue({
          ...hook,
          ...toFormValues(hook),
          workStationIds: hook.workStationIds || [],
        })
        setShowForm(true)
      }

      const handleToggleStatus = async (id, isActive) => {
        try {
          await hookService.toggleHookStatus(id, isActive)
          message.success(t('hooks.toggleSuccess'))
          loadHooks()
        } catch (error) {
          message.error(t('hooks.toggleError'))
        }
      }

      const handleSubmit = async (values) => {
        try {
          const payload = toPayload(values)
          if (editingHook) {
            await hookService.updateHook(editingHook.id, payload)
            message.success(t('hooks.updateSuccess'))
          } else {
            await hookService.createHook(payload)
            message.success(t('hooks.createSuccess'))
          }
          form.resetFields()
          setEditingHook(null)
          setShowForm(false)
          loadHooks()
        } catch (error) {
          const data = error && error.response && error.response.data
          message.error((data && (data.message || data.Message)) || t('hooks.saveError'))
        }
      }

      const handleViewDetails = (id) => {
        setSelectedHookId(id)
        setDetailsModalOpen(true)
      }

      const handleCancel = () => {
        setEditingHook(null)
        form.resetFields()
        setShowForm(false)
      }

      const columns = [
        {
          title: t('hooks.name'),
          dataIndex: 'address',
          key: 'address',
          width: '15%',
          ellipsis: true,
        },
        {
          title: t('hooks.description'),
          dataIndex: 'description',
          key: 'description',
          width: '15%',
          ellipsis: true,
        },
        {
          title: t('hooks.eventType'),
          dataIndex: 'hookType',
          key: 'hookType',
          width: '30%',
          render: (eventType) => {
            const event = HOOK_EVENTS.find((e) => e.id === eventType)
            if (!event) return eventType
            return (
              <div>
                <div>{t(`hooks.events.${event.name}.name`)}</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                  {t(`hooks.events.${event.name}.description`)}
                </div>
              </div>
            )
          },
        },
        {
          title: t('hooks.triggerType.title'),
          dataIndex: 'hookTrigger',
          key: 'hookTrigger',
          width: '15%',
          ellipsis: true,
          render: (trigger) => triggerLabel(t, trigger),
        },
        {
          title: t('hooks.authType.title'),
          dataIndex: 'authType',
          key: 'authType',
          width: '15%',
          ellipsis: true,
          render: (auth) => {
            switch (auth) {
              case 0:
                return t('hooks.authType.undefined')
              case 1:
                return t('hooks.authType.basic')
              case 2:
                return t('hooks.authType.bearer')
              case 3:
                return t('hooks.authType.oauth')
              default:
                return auth
            }
          },
        },
        {
          title: t('hooks.status.title'),
          dataIndex: 'isActive',
          key: 'isActive',
          width: '10%',
          align: 'center',
          sorter: (a, b) => {
            if (a.isActive === b.isActive) return 0
            return a.isActive ? -1 : 1
          },
          filters: [
            { text: t('hooks.status.active'), value: true },
            { text: t('hooks.status.inactive'), value: false },
          ],
          onFilter: (value, record) => record.isActive === value,
          render: (isActive, record) => (
            <Switch
              checked={isActive}
              onChange={(checked) => handleToggleStatus(record.id, checked)}
              loading={loading}
            />
          ),
        },
        {
          title: t('hooks.actions'),
          key: 'actions',
          width: '15%',
          align: 'center',
          render: (_, record) => (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => handleViewDetails(record.id)}
              />
              <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
            </div>
          ),
        },
      ]

      return (
        <>
          <Modal
            title={t('hooks.title')}
            open={isOpen}
            onCancel={onClose}
            width={1200}
            footer={null}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                {t('hooks.create')}
              </Button>

              {showForm && (
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                  <Form.Item
                    name="description"
                    label={t('hooks.modal.form.description')}
                    rules={[{ required: true, message: t('hooks.descriptionRequired') }]}
                  >
                    <Input placeholder={t('hooks.modal.form.descriptionPlaceholder')} />
                  </Form.Item>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <Form.Item
                      name="hookType"
                      label={t('hooks.modal.form.eventType')}
                      rules={[{ required: true, message: t('hooks.eventTypeRequired') }]}
                    >
                      <Select
                        placeholder={t('hooks.modal.form.eventTypePlaceholder')}
                        optionLabelProp="label"
                        onChange={(value) => {
                          onHookTypeChange(form, value)
                          if (value === 10 || value === 11 || value === 13 || value === 14) {
                            form.setFieldValue('hookTrigger', 0)
                          }
                          // integração de ordens não depende de estação
                          if (value === 14) {
                            form.setFieldsValue({ allWorkStations: true, workStationsIds: [] })
                          }
                        }}
                      >
                        {HOOK_EVENTS.map((event) => (
                          <Select.Option
                            key={event.id}
                            value={event.id}
                            label={t(`hooks.events.${event.name}.name`)}
                          >
                            <div>
                              <div>{t(`hooks.events.${event.name}.name`)}</div>
                              <div
                                style={{ fontSize: '12px', color: '#666', whiteSpace: 'normal' }}
                              >
                                {t(`hooks.events.${event.name}.description`)}
                              </div>
                            </div>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.hookType !== currentValues.hookType
                      }
                    >
                      {({ getFieldValue }) => {
                        const eventType = getFieldValue('hookType')
                        const isValidationEvent =
                          eventType === 10 ||
                          eventType === 11 ||
                          eventType === 13 ||
                          eventType === 14

                        if (isValidationEvent) {
                          return (
                            <div style={{ marginBottom: 24 }}>
                              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                                {t('hooks.validation.responseFormat')}
                              </div>
                              <div style={{ maxWidth: 400 }}>
                                <SyntaxHighlighter
                                  language="json"
                                  style={arta}
                                  customStyle={{
                                    margin: 0,
                                    padding: '12px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  {eventType === 14
                                    ? INTEGRATION_RESPONSE_SAMPLE
                                    : `{
  "errors": [
    {
      "canEndWorkOrder": true,
      "message": "Essa ordem não pode ser apontada por motivo x"
    }
  ]
}`}
                                </SyntaxHighlighter>
                              </div>
                              <div style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
                                {eventType === 14
                                  ? t('hooks.integration.description')
                                  : t('hooks.validation.description')}
                              </div>
                            </div>
                          )
                        }

                        return (
                          <Form.Item
                            name="hookTrigger"
                            label={t('hooks.modal.form.triggerType')}
                            rules={[{ required: true, message: t('hooks.triggerTypeRequired') }]}
                          >
                            <Select placeholder={t('hooks.modal.form.triggerTypePlaceholder')}>
                              {triggerOptions(t, eventType)}
                            </Select>
                          </Form.Item>
                        )
                      }}
                    </Form.Item>

                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.hookTrigger !== currentValues.hookTrigger ||
                        prevValues.hookType !== currentValues.hookType
                      }
                    >
                      {({ getFieldValue }) => {
                        const eventType = getFieldValue('hookType')
                        const triggerType = getFieldValue('hookTrigger')
                        const isValidationEvent =
                          eventType === 10 ||
                          eventType === 11 ||
                          eventType === 13 ||
                          eventType === 14
                        const isHttpPost = triggerType === 0 || isValidationEvent

                        return isHttpPost ? (
                          <Form.Item
                            name="authType"
                            label={t('hooks.modal.form.authType')}
                            rules={[{ required: true, message: t('hooks.authTypeRequired') }]}
                          >
                            <Select placeholder={t('hooks.modal.form.authTypePlaceholder')}>
                              <Select.Option value={0}>
                                {t('hooks.authType.undefined')}
                              </Select.Option>
                              <Select.Option value={1}>{t('hooks.authType.basic')}</Select.Option>
                              <Select.Option value={2}>{t('hooks.authType.bearer')}</Select.Option>
                              <Select.Option value={3}>{t('hooks.authType.oauth')}</Select.Option>
                            </Select>
                          </Form.Item>
                        ) : null
                      }}
                    </Form.Item>
                  </div>

                  {/* depois do tipo de evento: URL do webhook ou, em riscos do planejamento, os e-mails */}
                  <AddressField t={t} />

                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.hookTrigger !== currentValues.hookTrigger ||
                      prevValues.authType !== currentValues.authType
                    }
                  >
                    {({ getFieldValue }) =>
                      getFieldValue('hookTrigger') === 0 &&
                      getFieldValue('authType') !== 0 && (
                        <Form.Item name="authToken" label={t('hooks.modal.form.authToken')}>
                          <Input.TextArea
                            placeholder={t('hooks.modal.form.authTokenPlaceholder')}
                          />
                        </Form.Item>
                      )
                    }
                  </Form.Item>

                  {/* integração de ordens (tipo 14) não depende de estação */}
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.hookType !== currentValues.hookType
                    }
                  >
                    {({ getFieldValue }) =>
                      getFieldValue('hookType') !== 14 && (
                        <>
                          <Form.Item
                            name="allWorkStations"
                            valuePropName="checked"
                            label={t('hooks.modal.form.allWorkStations')}
                          >
                            <Switch />
                          </Form.Item>

                          <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                              prevValues.allWorkStations !== currentValues.allWorkStations
                            }
                          >
                            {({ getFieldValue }) =>
                              !getFieldValue('allWorkStations') && (
                                <Form.Item
                                  name="workStationsIds"
                                  label={t('hooks.modal.form.workStations')}
                                  rules={[
                                    {
                                      required: true,
                                      message: t('hooks.workStationsRequired'),
                                    },
                                  ]}
                                >
                                  <Select
                                    mode="multiple"
                                    placeholder={t('hooks.modal.form.workStationsPlaceholder')}
                                    style={{ width: '100%' }}
                                    optionFilterProp="children"
                                  >
                                    {workStations.map((ws) => (
                                      <Select.Option key={ws.id} value={ws.id}>
                                        {ws.description}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              )
                            }
                          </Form.Item>
                        </>
                      )
                    }
                  </Form.Item>

                  <Form.Item
                    name="isActive"
                    valuePropName="checked"
                    label={t('hooks.modal.form.isActive')}
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button type="primary" htmlType="submit">
                        {editingHook ? t('hooks.update') : t('hooks.create')}
                      </Button>
                      <Button onClick={handleCancel}>{t('hooks.cancel')}</Button>
                      <TestSendButton t={t} form={form} />
                    </div>
                  </Form.Item>
                </Form>
              )}

              <Table
                columns={columns}
                dataSource={hooks}
                loading={loading}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  showTotal: (total) => `Total: ${total}`,
                }}
                scroll={{ x: 800 }}
              />
            </div>
          </Modal>

          <HookDetailsModal
            isOpen={detailsModalOpen}
            onClose={() => setDetailsModalOpen(false)}
            hookId={selectedHookId}
            workStations={workStations}
          />
        </>
      )
    })
  )
)

export default withNamespaces()(HooksModal)
